/**
 * The farm auction card — Pass 17 rev B, `17g` at 1400 and `17h` at 390.
 *
 * "One slot, six states, and it never moves." A `min-height` on the card, not a shape per state,
 * so the rail never reflows mid-bid — the worst thing a bidding UI can do is move the Raise
 * button as somebody else raises.
 *
 *   WAITING keeps the controls, inert, so the shape of what is coming is already familiar.
 *   YOUR NOMINATION is the only state that changes colour, because it is the only one where the
 *     app is waiting on you rather than on the room. The prospect field sits ON the card now (17g);
 *     the list of matches stays in the panel below, so the card's height never depends on how many
 *     prospects match.
 *   YOU HOLD IT says so in the eyebrow and dims every raise: nobody bids against himself, and with
 *     a going-gone clock that resets on every raise a mis-tap here extends a lot already won.
 *   OUTBID is the sixth reader, and the state the whole redesign was for: a GM who bid, was
 *     passed, and is looking at his own phone. "Rajan holds it at ₣12. You are out unless you
 *     raise." The primary button names the price.
 *   BLOCKED greys the steps with the reason, because a GM should not bid for twenty seconds before
 *     the app tells him he was never eligible (2017-II-5 §6).
 *   SOLD lingers with the price and the consequence, then the next nomination opens.
 *   NOMINATING FOR RYAN is the commissioner's amber variant, the same grammar as ENTERING FOR on
 *     the board: Ryan is up and not on a phone.
 *
 * WHO MAY PRESS THESE BUTTONS. `firestore.rules` still says writes to `farm/` are commissioner-only,
 * so on the live site the raise and pass controls act for a commissioner recording what the room
 * calls out, and every other manager gets the same card read-only with the live price on it. The
 * card is designed for each GM bidding from his own phone; the rules change is Tim's.
 */

import { faab } from '../domain/faab.js';
import { managerShort } from '../domain/identity.js';
import { leftIfYouWin, nomCode, raiseSteps, type AuctionView } from '../domain/farmAuction.js';
import { Citations } from './RuleText.js';

interface Props {
  view: AuctionView;
  /** FAAB the viewer has to spend, or null when it is not known. */
  faabLeft: number | null;
  /** Farm players the viewer holds tonight, and the cap. */
  farm?: { held: number; cap: number };
  /** Whether this actor may actually write — a commissioner, per the Firestore rules. */
  canAct: boolean;
  /** Why not, in the words that fit the person reading. */
  whyNot?: string;
  /**
   * The viewer's own standing on this lot, tracked by the screen: the last amount he bid, or null
   * when he has not. With the high bidder this is what makes OUTBID a state rather than a guess.
   */
  myBid?: number | null;
  /** The sale still on the card, if one just closed. */
  sold: {
    player: string; winner: string; bid: number; left: number;
    /** What it did to the winner: his farm count and budget after this. Optional, stated when known. */
    winnerFarm?: { held: number; cap: number }; winnerBudget?: number | null;
    /** Whose nomination opens when the dwell ends. */
    next?: string | null;
  } | null;
  /**
   * The clock for this state: the nomination clock while somebody is choosing, the going-gone
   * clock once bidding is open. Null when the commissioner has set no length.
   */
  clock: { left: number; over: boolean; text: string } | null;
  /** Why the last write was refused — a race somebody else won. Shown at the controls. */
  refused: string | null;
  onRaise: (to: number) => void;
  /** Pass on the open lot: a deliberate exit that costs nothing. */
  onPass: () => void;
  /** Pass the NOMINATION — the man who is up names nobody and the turn moves on. */
  onSkipNomination?: () => void;
  /**
   * The prospect field on the card. The card owns the text; the panel below owns the matches. A
   * screen that does not pass these gets the old behaviour: a Nominate button that focuses the
   * panel's own search.
   */
  query?: string;
  onQuery?: (q: string) => void;
  /**
   * What the typed text matches, for the card to offer under the field.
   *
   * THE MATCHES CAME UP HERE WHEN THE PANEL BELOW WENT (Tim, 9 September: "remove the panel
   * labeled 'round 1, nomination 3 - ryan'"). That panel held the field's matches and their
   * `Choose` buttons; the field without them is a box you must spell a name into exactly. Six is
   * as many as belong on a card — the rail's board is the list to browse.
   */
  matches?: ReadonlyArray<{ name: string; pos?: string; school?: string; rank?: number }>;
  onChoose?: (name: string) => void;
  /** Open the bidding on the man now standing on the card (or on the typed name). */
  onNominate: () => void;
  /**
   * THE MAN CHOSEN, BEFORE HE IS NOMINATED (Tim, 7 September). Tapping a row in the panel below
   * used to open the bidding on him there and then, which is a lot of consequence for one tap in
   * a room. He stands on the card instead — name, position, school and a way out — and the
   * Nominate button is what tells the league.
   */
  chosen?: { name: string; pos?: string; school?: string } | null;
  onClearChosen?: () => void;
  /**
   * The commissioner's variant: when it is somebody else's nomination and the viewer may enter it
   * for him, the card turns amber and names him. Null for everybody else.
   */
  nominateFor?: string | null;
  /** `WR · Ohio State` — under the name. */
  detail?: string;
  /** The next three nominators after this turn, for the phone's foot (17h). */
  upNext?: readonly string[];
  /** The manager looking at the card, for "you" in the nominator list. */
  me?: string;
  children?: React.ReactNode;
}


export function AuctionCard(
  { view, faabLeft, farm, canAct, whyNot, myBid = null, sold, clock, refused, onRaise, onPass,
    onSkipNomination, query, onQuery, onNominate, nominateFor = null, detail, upNext, me,
    chosen = null, onClearChosen, matches, onChoose, children }: Props,
) {
  const { state, turn, high, nextBid, youAreHigh } = view;

  if (sold) {
    const w = managerShort(sold.winner);
    const after = [
      sold.winnerFarm ? `${w}'s farm is now ${sold.winnerFarm.held} of ${sold.winnerFarm.cap}` : null,
      sold.winnerBudget != null ? `his budget ${faab(sold.winnerBudget)}` : null,
    ].filter(Boolean).join(' and ');
    return (
      <section className="card auctioncard sold">
        <div className="achead">
          <span className="actag good">Sold</span>
          <span className="acaside">next up in 0:{String(sold.left).padStart(2, '0')}</span>
        </div>
        <p className="acline">
          <b>{sold.player}</b> to {w} for <b className="acprice">{faab(sold.bid)}</b>.
        </p>
        <p className="acnote">
          {after ? `${after}. ` : ''}The row is in the farm log.
        </p>
        <p className="acnote mut">
          {sold.next
            ? `Lingers, then ${managerShort(sold.next)}'s nomination opens.`
            : 'Lingers, then the farm rounds close.'}
          {faabLeft !== null && sold.winner.toUpperCase() !== (me ?? '').toUpperCase()
            ? ` Your ${faab(faabLeft)} is untouched.` : ''}
        </p>
        <Citations ids={['2017-II-5']} />
      </section>
    );
  }

  if (state === 'over' || !turn) {
    return (
      <section className="card auctioncard">
        <div className="achead"><span className="actag">Farm rounds complete</span></div>
        <p className="acnote">Every nomination is in.</p>
      </section>
    );
  }

  const code = nomCode(turn.round, turn.slot);
  const clockChip = clock && (
    <span className={`acclock ${clock.over || clock.left <= 5 ? 'over' : ''}`}>{clock.text}</span>
  );

  /* -- the nomination, mine or entered for somebody ------------------------------------- */
  if (state === 'nominate' || nominateFor) {
    const forWhom = state === 'nominate' ? null : nominateFor;
    const shown = (query ?? '').trim() && !chosen ? (matches ?? []).slice(0, 6) : [];
    const field = onQuery ? (
      <>
        <label className="acsearch">
          <span aria-hidden="true">⌕</span>
          <input type="search" value={query ?? ''} placeholder="Prospect, or type any name…"
            aria-label="Prospect to nominate"
            onChange={(e) => onQuery(e.target.value)} />
        </label>
        {shown.length > 0 && onChoose && (
          <ul className="acmatches">
            {shown.map((m) => (
              <li key={m.name}>
                <button type="button" title={`Put ${m.name} on the card`}
                  onClick={() => onChoose(m.name)}>
                  {m.rank != null && <span className="acrank">{m.rank}</span>}
                  <b>{m.name}</b>
                  {m.pos && <span className={`poschip ident pos-${m.pos}`}>{m.pos}</span>}
                  {m.school && <span className="mut acschool">{m.school}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </>
    ) : null;
    return (
      <section className={`card auctioncard ${forWhom ? 'nomfor' : 'nominate'}`}>
        {/*
          THE HEAD, IN THE ORDER A MAN READS IT (Tim, 7 September). `Commissioner` is a badge on
          the card, not a clause in its title, so it goes above in the small type every eyebrow on
          this page uses; the line that matters — whose nomination this is — is the biggest thing
          on the card, with his name bold in it; and the pick code drops to its own line, where a
          reference number belongs.
        */}
        <div className="achead">
          {forWhom && <span className="actag amber">Commissioner</span>}
          {clockChip}
        </div>
        <h4 className="acnomwho">
          {forWhom ? <>Nominating for <b>{managerShort(forWhom)}</b></> : <>Your <b>nomination</b></>}
        </h4>
        <p className="acnomcode">{code}</p>
        {field}
        {/*
          The chosen man, standing where the explanation used to be: it said the same three things
          on every turn of a twenty-turn auction, and the one thing the card could not say was who
          you had just picked out of the list.
        */}
        {chosen && (
          <div className="acchosen">
            <b>{chosen.name}</b>
            {chosen.pos && <span className={`poschip ident pos-${chosen.pos}`}>{chosen.pos}</span>}
            {chosen.school && <span className="acschool">{chosen.school}</span>}
            {onClearChosen && (
              <button type="button" className="acx" title={`Not ${chosen.name}`}
                aria-label={`Clear ${chosen.name}`} onClick={onClearChosen}>&times;</button>
            )}
          </div>
        )}
        <div className="acacts">
          <button type="button" className="btn primary acnom"
            disabled={!chosen && !(query ?? '').trim()}
            title={chosen ? `Open the bidding on ${chosen.name}`
              : 'Pick a prospect from the list below, or type a name'}
            onClick={onNominate}>
            Nominate
          </button>
          <button type="button" className="btn acpass" onClick={onSkipNomination ?? onPass}>
            Pass
          </button>
        </div>
        {children}
      </section>
    );
  }

  const steps = raiseSteps(nextBid);
  const inert = (
    <div className="acsteps" aria-hidden="true">
      {steps.map(({ step }) => (
        <button type="button" className="btn" key={step} disabled>+{faab(step)}</button>
      ))}
      <button type="button" className="btn acpass" disabled>Pass</button>
    </div>
  );

  if (state === 'waiting') {
    return (
      <section className="card auctioncard waiting">
        <div className="achead">
          <span className="actag">Waiting</span>
          {clockChip}
          <span className="acaside">{clock ? 'to nominate' : code}</span>
        </div>
        {/*
          WHOSE TURN IT IS, AND THEN WHAT HAPPENS NEXT (Tim, 7 September). The budget rode this
          line and pushed the sentence onto two; it is a standing fact about the viewer rather
          than news about the auction, so it has a strip of its own below the card. And "nothing
          for you to do yet" was the card telling a man he was not needed — the sentence after it
          already says when he will be.
        */}
        <p className="acline">
          <b>{managerShort(turn.nominator)}</b>&rsquo;s nomination is next.
        </p>
        <p className="acnote mut">When he names a player, bidding opens for everyone at once.</p>
        {inert}
      </section>
    );
  }

  const player = turn.rec?.player ?? '';
  const nominatedBy = `nominated by ${managerShort(turn.nominator)}`;
  const sub = [player, detail, nominatedBy].filter(Boolean).join(' · ');

  if (state === 'blocked') {
    return (
      <section className="card auctioncard blocked">
        <div className="achead">
          <span className="actag warn">Blocked</span>
          {clockChip}
          <span className="acaside">{view.blockedCount}</span>
        </div>
        <p className="acline">{view.blockedBecause} You cannot bid on this nomination.</p>
        <p className="acsub">{sub}</p>
        {inert}
        <p className="acnote mut">
          The yearly-allowance block (2017-II-5 §6) lands at the point of sale, not at bidding.
        </p>
      </section>
    );
  }

  /* -- bidding: you hold it, you are out, or you have not spoken ------------------------ */
  const outbid = Boolean(high && !youAreHigh && myBid != null);
  const dead = !canAct || youAreHigh;
  const title = (to: number) =>
    youAreHigh ? 'You already hold it' : canAct ? `Bid ${faab(to)}` : whyNot;
  const tag = youAreHigh ? 'Bidding · you hold it' : outbid ? 'Outbid' : 'Bidding open';
  const passTitle = youAreHigh
    ? 'You cannot pass on a lot you are winning'
    : canAct ? 'Takes you off this one — it costs nothing' : whyNot;

  return (
    <section className={`card auctioncard ${youAreHigh ? 'holding' : outbid ? 'outbid' : 'bidding'}`}>
      <div className="achead">
        <span className={`actag ${outbid ? 'warn' : 'accent'}`}>{tag}</span>
        {clockChip}
        <span className="acaside">{code}</span>
      </div>

      {youAreHigh ? (
        <p className="acline"><b>{player}</b> · your {faab(high!.amount)} stands</p>
      ) : high ? (
        <p className="acline">
          <b>{managerShort(high.gm)} holds it at {faab(high.amount)}.</b>
          {outbid ? ' You are out unless you raise.' : ''}
        </p>
      ) : (
        <p className="acline"><b>{player}</b> · opens at {faab(nextBid)}</p>
      )}
      <p className="acsub">{youAreHigh ? [detail, nominatedBy].filter(Boolean).join(' · ') : sub}</p>

      <p className="acnote">
        {/*
          While he is winning, the figure that matters is what the bid LEAVES him. Otherwise the
          standing budget is on its own strip below the card and does not need repeating here.
        */}
        {youAreHigh && faabLeft !== null
          ? `Budget after this bid ${faab(leftIfYouWin(faabLeft, high!.amount))}`
            + (farm ? ` · farm ${farm.held} of ${farm.cap}` : '')
          : ''}
        {!youAreHigh && clock && (
          <span className="acgoing">
            {' '}Going clock <b>{clock.text}</b>
            {high ? ' — it sells to him when it runs out.' : ''}
          </span>
        )}
      </p>

      {youAreHigh ? inert : (
        <div className="acsteps">
          <button type="button" className="btn primary acraise" disabled={dead}
            title={canAct ? undefined : whyNot} onClick={() => onRaise(nextBid)}>
            Raise to {faab(nextBid)}
          </button>
          {steps.slice(1).map(({ step, to }) => (
            <button type="button" className="btn" key={step} disabled={dead}
              title={title(to)} onClick={() => onRaise(to)}>
              +{faab(step)}
            </button>
          ))}
          <button type="button" className="btn acpass" disabled={dead} title={passTitle}
            onClick={onPass}>
            {outbid ? 'Let it go' : 'Pass'}
          </button>
        </div>
      )}

      {youAreHigh && (
        <p className="acnote mut">The room bids from here. Nothing to do until somebody goes over you.</p>
      )}
      {refused && <p className="acrefused">{refused}</p>}

      {upNext && upNext.length > 0 && (
        <p className="acnext ponly">
          <span className="mut">Next nominators</span>
          {upNext.map((g, i) => (
            <span key={`${g}${i}`}>
              {i > 0 && <i aria-hidden="true">→</i>}
              {g.toUpperCase() === (me ?? '').toUpperCase() ? 'you' : managerShort(g)}
            </span>
          ))}
        </p>
      )}
    </section>
  );
}
